<script setup lang="ts">
import { computed, ref } from 'vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import MaskSelectorModal from '../MaskSelectorModal.vue';
import { useToaster } from '@/composables/toaster';
import { MaskType } from '@/utils/enums/maskType.enum';
import CalendarColorField from './CalendarColorField.vue';
import CalendarFieldTitle from './CalendarFieldTitle.vue';
import {
    CALENDAR_MARKER_OPTIONS,
    CUSTOM_MARKER_MAX_KB,
    DEFAULT_CUSTOM_MARKER_SCALE,
    DEFAULT_CALENDAR_MARKER,
    findPresetMarker,
    getCalendarMarkerPresets,
    loadCustomMarker,
    renderMarkerThumbnail
} from './calendarUtils';
import { useCalendarConfig } from './useCalendarConfig';
import type { MaskDto } from '@/api/typings/mask';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CalendarConfig } from './types';

// MaskType is a const enum, so the template can't reference it directly
const MARKER_MASK_TYPE = MaskType.CalendarMarker;

// Vue 2.7 SFC compiler cannot resolve imported types in defineProps
const props = defineProps<{
    imageElement?: ImagePlaceholderElement;
}>();
const emit = defineEmits<{
    (e: 'update-config', config: Partial<CalendarConfig>): void;
}>();

const { config, elementKey, disabled, update } = useCalendarConfig(props, emit);
const toaster = useToaster();

const modalOpen = ref(false);
const markerPresets = ref<MaskDto[]>([]);

const isCustomMarker = computed(() => config.value?.markerType === 'custom' && !!config.value.customMarker);

const presetMarker = computed(() =>
    CALENDAR_MARKER_OPTIONS.find((option) => option.value === config.value?.markerType)
);

const markerThumbnail = computed(() =>
    config.value ? renderMarkerThumbnail(config.value.markerType, config.value.customMarker) : ''
);

const openModal = () => {
    if (disabled.value) return;
    // Built on first open, measuring the markers needs the DOM
    if (!markerPresets.value.length) markerPresets.value = getCalendarMarkerPresets();
    modalOpen.value = true;
};

const onMarkerSelected = async (mask: MaskDto) => {
    modalOpen.value = false;

    const preset = findPresetMarker(mask);
    if (preset) {
        update({ markerType: preset, customMarker: '', customMarkerPath: '' });
        return;
    }

    try {
        update(await loadCustomMarker(mask.path, currentDayFontSize(), config.value?.customMarkerScale));
    } catch (error) {
        toaster.error(`The marker ${mask.name} could not be loaded: ${(error as Error).message}`);
    }
};

// Read from the element, the local copy misses font size changes made in the Days section
const currentDayFontSize = () => props.imageElement?.calendarConfig?.dayFontSize ?? config.value?.dayFontSize ?? '';

const onMarkerScaleChange = async (value: string | number = 0) => {
    const scale = Number(value);
    const path = config.value?.customMarkerPath;
    if (!path || !(scale > 0)) return;

    try {
        update(await loadCustomMarker(path, currentDayFontSize(), scale));
    } catch (error) {
        toaster.error(`The marker could not be resized: ${(error as Error).message}`);
    }
};

const removeCustomMarker = () => {
    update({
        markerType: DEFAULT_CALENDAR_MARKER,
        customMarker: '',
        customMarkerPath: '',
        customMarkerScale: undefined
    });
};

const onMarkerColorChange = (color: string) => {
    update({ markColor: color, markBorderColor: color });
};
</script>

<template>
    <div v-if="config" :key="elementKey" class="calendar-section">
        <calendar-color-field
            title="Selected Date Font Color"
            :value="config.dayColorHighlight"
            :disabled="disabled"
            @input="update({ dayColorHighlight: $event })"
        />

        <calendar-field-title>{{ isCustomMarker ? 'Marker Image' : 'Marker Image & Color' }}</calendar-field-title>
        <div class="marker-row rounded" :class="{ disabled }" data-testid="calendar-marker-row" @click="openModal">
            <span class="marker-thumbnail">
                <img v-if="markerThumbnail" :src="markerThumbnail" :alt="presetMarker?.label ?? 'Custom marker'" />
            </span>
            <span class="flex-fill text-truncate">{{ isCustomMarker ? 'Custom' : presetMarker?.label }}</span>
            <c-button size="icon" :icon="['far', 'arrows-rotate']" :disabled="disabled" @click.stop="openModal" />
            <c-button
                v-if="isCustomMarker"
                size="icon"
                :icon="['far', 'trash-can']"
                :disabled="disabled"
                @click.stop="removeCustomMarker"
            />
        </div>

        <!-- The lib draws uploaded markers as-is, color and fill only apply to the built-in ones -->
        <!-- Markers picked before the path was stored can't be rebuilt, so they can't be resized either -->
        <c-input
            v-if="isCustomMarker && config.customMarkerPath"
            :value="config.customMarkerScale ?? DEFAULT_CUSTOM_MARKER_SCALE"
            type="decimal"
            step=".1"
            :min="0.1"
            label="Marker Scale"
            data-testid="calendar-marker-scale"
            :disabled="disabled"
            @input="onMarkerScaleChange"
        />
        <template v-if="!isCustomMarker">
            <calendar-color-field :value="config.markColor" :disabled="disabled" @input="onMarkerColorChange" />
            <c-checkbox
                :value="config.markFill"
                label="Fill Marker"
                :disabled="disabled"
                @input="update({ markFill: $event })"
            />
        </template>

        <mask-selector-modal
            v-if="modalOpen"
            :type="MARKER_MASK_TYPE"
            :presets="markerPresets"
            title="Select or Upload a Marker"
            accept=".svg"
            :max-kb-size="CUSTOM_MARKER_MAX_KB"
            @select="onMarkerSelected"
            @cancel="modalOpen = false"
            @error="toaster.error($event)"
        />
    </div>
</template>

<style scoped lang="scss">
@import './calendarSection';

.marker-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.625rem;
    background-color: #ebe8f7;
    cursor: pointer;

    &.disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    .marker-thumbnail {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        background-color: #fff;
        border-radius: 0.25rem;

        img {
            max-width: 100%;
            max-height: 100%;
        }
    }
}
</style>
